<script setup>
import { computed } from 'vue';
import Modal from 'dashboard/components/Modal.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter';

const props = defineProps({
  show: { type: Boolean, default: false },
  plan: { type: Object, default: null },
  isCurrentPlan: { type: Boolean, default: false },
  isPlanExpired: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'upgradeSuccess']);

const formatMarkdown = text => {
  if (!text) return '';
  const formatter = new MessageFormatter(text);
  return formatter.formattedMessage;
};

const formatPrice = price => {
  if (price === null || price === undefined) return '$0';
  return `$${price}`;
};

const planDescription = computed(() => {
  return props.plan?.description || '';
});

const showChangeWarning = computed(() => {
  return props.isCurrentPlan && !props.isPlanExpired;
});
</script>

<template>
  <Modal :show="show" @close="emit('close')">
    <div class="w-full max-w-2xl">
      <div
        class="flex items-center justify-between p-6 border-b border-n-slate-6"
      >
        <div class="flex items-center gap-3">
          <h2 class="text-2xl font-semibold text-n-slate-12">
            {{ plan?.name }}
          </h2>
          <span
            v-if="isCurrentPlan"
            class="px-2 py-1 text-xs font-medium rounded-md bg-n-brand/10 text-n-brand"
          >
            {{ $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.CURRENT_PLAN') }}
          </span>
        </div>
        <button
          class="text-n-slate-11 hover:text-n-slate-12"
          @click="emit('close')"
        >
          <fluent-icon icon="dismiss" size="24" />
        </button>
      </div>

      <div class="p-6 max-h-[70vh] overflow-y-auto">
        <div class="mb-6 flex items-baseline gap-2">
          <span class="text-4xl font-bold text-n-slate-12">
            {{ formatPrice(plan?.price_month) }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.PER_MONTH') }}
          </span>
          <span v-if="plan?.price_year" class="text-sm text-n-slate-11 ml-4">
            {{ formatPrice(plan?.price_year) }}
            {{ $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.PER_YEAR') }}
          </span>
        </div>

        <div
          v-if="planDescription"
          v-dompurify-html="formatMarkdown(planDescription)"
          class="mb-6 text-sm text-n-slate-11 leading-relaxed prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_p]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mb-1 [&_strong]:font-semibold [&_em]:italic [&_code]:bg-n-slate-3 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_a]:text-n-brand [&_a]:underline"
        />

        <div
          v-if="showChangeWarning"
          class="mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-3"
        >
          <Icon
            name="i-lucide-alert-triangle"
            class="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
          />
          <div>
            <p class="text-sm font-medium text-amber-800">
              {{
                $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.CHANGE_WARNING_TITLE')
              }}
            </p>
            <p class="text-xs text-amber-700 mt-1">
              {{
                $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.CHANGE_WARNING_DESC')
              }}
            </p>
          </div>
        </div>

        <div
          v-if="
            Number(plan?.price_month) > 0 && (!isCurrentPlan || isPlanExpired)
          "
          class="mb-6"
        >
          <button
            class="w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors bg-n-brand text-white hover:bg-n-brand/90 flex items-center justify-center gap-2"
            @click="emit('upgradeSuccess', plan)"
          >
            <Icon name="i-lucide-credit-card" class="w-4 h-4" />
            {{ $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.PAYPAL') }}
          </button>
        </div>
      </div>
    </div>
  </Modal>
</template>
